import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Download, Zap } from 'lucide-react';

const API = '/api';

const STYLE_PRESETS = [
  { value: 'professional', label: 'Professional' },
  { value: 'headshot', label: 'Headshot' },
  { value: 'logo', label: 'Logo' },
  { value: 'banner', label: 'Banner' },
  { value: 'abstract', label: 'Abstract' },
];

const PROVIDERS = [
  {
    value: 'openai',
    label: 'DALL-E 3',
    desc: 'OpenAI · HD quality · ~15s',
    icon: '🎨',
    color: 'border-green-400 bg-green-50 text-green-800',
    activeBg: 'bg-green-600',
  },
  {
    value: 'grok',
    label: 'Grok Aurora',
    desc: 'xAI · Fast · Creative',
    icon: '⚡',
    color: 'border-violet-400 bg-violet-50 text-violet-800',
    activeBg: 'bg-violet-600',
  },
];

const PROMPT_SUGGESTIONS = [
  'Professional executive headshot, confident smile, office background, sharp focus',
  'Modern minimalist business logo, blue and white colors, clean design',
  'Professional email banner, corporate services, clean gradient background',
  'Abstract geometric background, blue and purple gradient, modern style',
  'Corporate team meeting, diverse professionals, bright modern office',
];

export default function AIImages() {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('professional');
  const [provider, setProvider] = useState('openai');
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/ai/history`)
      .then(r => setHistory(r.data))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, []);

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post(`${API}/ai/generate`, {
        prompt: prompt.trim(),
        style,
        provider,
      });
      setHistory(h => [res.data, ...h]);
      setPrompt('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Generation failed. Check your plan limits or API configuration.');
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = (url, id) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-image-${id}.png`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const activeProvider = PROVIDERS.find(p => p.value === provider);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <Sparkles className="w-7 h-7 text-amber-500" />
        <h1 className="text-2xl font-bold text-gray-900">AI Image Generator</h1>
      </div>

      {/* Generator card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8 shadow-sm">
        {/* Provider selector */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">AI Provider</label>
          <div className="flex gap-3">
            {PROVIDERS.map(p => (
              <button
                key={p.value}
                onClick={() => setProvider(p.value)}
                className={`flex-1 flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                  provider === p.value
                    ? `${p.color} border-current shadow-sm`
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <span className="text-2xl">{p.icon}</span>
                <div className="text-left">
                  <div className="font-semibold text-sm">{p.label}</div>
                  <div className="text-xs text-gray-500">{p.desc}</div>
                </div>
                {provider === p.value && (
                  <div className={`ml-auto w-4 h-4 rounded-full ${p.activeBg} flex items-center justify-center`}>
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Style presets */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Style</label>
          <div className="flex gap-2 flex-wrap">
            {STYLE_PRESETS.map(s => (
              <button
                key={s.value}
                onClick={() => setStyle(s.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  style === s.value
                    ? 'bg-amber-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Prompt textarea */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">Prompt</label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Describe the image you want to generate in detail..."
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
            rows={3}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) generate(); }}
          />
        </div>

        {/* Prompt suggestions */}
        <div className="mb-4">
          <p className="text-xs text-gray-400 mb-2">Quick prompts:</p>
          <div className="flex flex-wrap gap-2">
            {PROMPT_SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                onClick={() => setPrompt(s)}
                className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2 py-1 rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors"
              >
                {s.length > 45 ? s.slice(0, 45) + '…' : s}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg mb-3 text-sm">{error}</div>
        )}

        <div className="flex items-center gap-4">
          <button
            onClick={generate}
            disabled={generating || !prompt.trim()}
            className={`flex items-center gap-2 text-white px-6 py-2.5 rounded-xl font-medium disabled:opacity-60 transition-colors ${
              provider === 'grok'
                ? 'bg-violet-600 hover:bg-violet-700'
                : 'bg-amber-500 hover:bg-amber-600'
            }`}
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : provider === 'grok' ? (
              <Zap className="w-4 h-4" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {generating
              ? `Generating with ${activeProvider?.label}...`
              : `Generate with ${activeProvider?.label}`
            }
          </button>
          <p className="text-xs text-gray-400">Ctrl+Enter to generate</p>
        </div>
      </div>

      {/* Gallery */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Generated Images</h2>
        <span className="text-sm text-gray-400">{history.length} images</span>
      </div>

      {loadingHistory ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-300 mx-auto" />
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <Sparkles className="w-14 h-14 mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400 font-medium">No images generated yet</p>
          <p className="text-gray-300 text-sm mt-1">Choose a provider and try the generator above!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {history.map(img => (
            <div
              key={img.id}
              className="group relative bg-gray-100 rounded-xl overflow-hidden aspect-square shadow-sm"
            >
              <img
                src={img.image_url}
                alt={img.prompt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Provider badge */}
              <div className={`absolute top-2 right-2 px-1.5 py-0.5 rounded text-xs font-bold ${
                img.provider === 'grok'
                  ? 'bg-violet-600 text-white'
                  : 'bg-green-600 text-white'
              }`}>
                {img.provider === 'grok' ? '⚡' : '🎨'}
              </div>
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3">
                <p className="text-white text-xs text-center line-clamp-4 leading-relaxed">
                  {img.prompt}
                </p>
                <button
                  onClick={() => downloadImage(img.image_url, img.id)}
                  className="flex items-center gap-1 bg-white text-gray-800 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors"
                >
                  <Download className="w-3 h-3" /> Download
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
